<template>
  <svg :width="width" :height="height">
    <g :transform="graphTransform">
      <g v-for="i in depth" :class="'depth' + depth" >
        <g v-for="(node, j) in nodes(i)" >
          <line
             stroke="silver"
             stroke-width="2"
             :x1="xLabel(i)"
             :x2="xLabel(i) + graphWidth * labelRatio / depth"
             :y1="yLabel(yLabelShift(nodes, i, j)) + size(node) * barHeight"
             :y2="yLabel(yLabelShift(nodes, i, j)) + size(node) * barHeight" />          
          <ellipsis
             v-if="!node.dummy"
             :width="textWidth(node)"
             :x="xLabel(i)"
             :y="yLabel(yLabelShift(nodes, i, j)) + 0.5 * size(node) * barHeight"
             dy="4"
             :text="node.label" />
        </g>
      </g>
      <g v-for="(leaf, i) in leaves" >
        <g v-for="value in leaf.values" class="bar hoverable" >
          <rect
            :fill="color(value.key)"
            :x="graphWidth * labelRatio + barLabelMargin"
            :width="x(value.value)"
            :y="i * barHeight + barGap / 2 + zScale(value.key) * (barHeight - barGap) / zDomain.length"
            :height="(barHeight - barGap) / zDomain.length" />
          <text
            :x="graphWidth * labelRatio + barLabelMargin + x(value.value)"
            :dx="5"
            :y="i * barHeight + barGap / 2 + (zScale(value.key) - 0.5) * (barHeight - barGap) / zDomain.length"
            :dy="barHeight / 2 + barGap / 2" >
            {{ yFormatter(value.value) }}
          </text>
        </g>
      </g>
    </g>
  </svg>
</template>

<style scoped>
.bar {
    opacity: 0.7;
}
.bar:hover {
    opacity: 1;
}
.bar.selected {
    stroke: red
}
</style>

<script>
import * as d3 from 'd3'
import * as d3Chromatic from 'd3-scale-chromatic'
import Graph from './Graph.vue'
import Ellipsis from './Ellipsis.vue'
import margin from '../mixins/margin.js'
import OrthogonalAxes from './OrthogonalAxes.vue'

export default {
  extends: Graph,
  mixins: [
    margin
  ],
  components: {
    Ellipsis: Ellipsis
  },
  props: {
    width: {
      type: Number,
      default: 960
    },
    height: {
      type: Number,
      default: 500
    },
    labelRatio: {
      type: Number,
      default: 0.2
    },
    barGap: {
      type: Number,
      default: 4
    },
    barLabelMargin: {
      type: Number,
      default: 5
    },
    barRightMargin: {
      type: Number,
      default: 40
    },
    domain: {
      type: Array,
      required: false
    },
    maxBarHeight: {
      type: Number,
      default: Infinity
    },
    sortReverse: {
      type: Boolean,
      default: false
    },
    colorScale: {
      default: () => {
        return d3.scaleSequential(d3Chromatic.interpolateSpectral)
      }
    }
  },
  computed: {
    zDomain () {
      return Array.from(new Set([].concat(...this.leaves.map(l => l.values.map(v => v.key)))))
    },
    keyColors () {
      const labels = {}
      this.leaves.forEach(l => {
        l.values.forEach(v => {
          labels[v.key] = v.label
        })
      })
      return this.color.domain().map(v => {
        return {
          label: labels[v],
          color: this.color(v)
        }
      })
    },
    color () {
      this.colorScale.domain([0, 100])
      return d3.scaleOrdinal().domain(this.zDomain).range(this.zDomain.map((_, i) => {
        if (this.zDomain.length < 2) {
          return this.colorScale(50)
        }
        return this.colorScale(100 * i / (this.zDomain.length - 1))
      }))
    },
    zScale () {
      return d3.scaleOrdinal().domain(this.zDomain).range([0, this.zDomain.length - 1])
    },
    barHeight () {
      return Math.min(this.graphHeight / this.span, this.maxBarHeight)
    },
    x () {
      return d3
        .scaleLinear()
        .domain(this.domain || [
          this.min === null ? 0 : this.min,
          this.max === null ? Math.max(...this.leaves.map(l => {
            return Math.max(...l.values.map(v => v.value))
          })) : this.max
        ])
        .clamp(true)
        .range([0, this.graphWidth - this.graphWidth * this.labelRatio - this.barLabelMargin - this.barRightMargin])
    },
    xLabel () {
      return d3
        .scaleLinear()
        .domain([1, this.depth + 1])
        .range([0, this.graphWidth * this.labelRatio])
    },
    yLabel () {
      return d3
        .scaleLinear()
        .domain([0, this.span])
        .range([0, this.barHeight * this.span])
    },
    yLabelShift () {
      return (nodes, i, j) => {
        return nodes(i).slice(0, j).map(node => this.size(node)).reduce((acc, s) => acc + s, 0)
      }
    },
    span () {
      return this.size({children: this.data})
    },
    depth () {
      return this.computeDepth({children: this.data}) - 1
    },
    leaves () {
      return this.nodes(this.depth)
    },
    nodes () {
      return depth => {
        let nodes
        for (let i = 0; i < depth; ++i) {
          nodes = nodes ? [].concat(...nodes.map(
            n => n.children && n.children.length ? n.children : [Object.assign({dummy: true}, n)]
          )) : this.data
        }
        return nodes.map(n => Object.assign({
          leaf: !n.children || !n.children.length,
          depth: depth
        }, n)).sort((n1, n2) => {
          if (n1.parent === n2.parent) {
            return n1.label.localeCompare(n2.label)
          }
          return n1.parent.localeCompare(n2.parent)
        })
      }
    }
  },
  methods: {
    computeDepth (node) {
      return node.children ? Math.max(...node.children.map(this.computeDepth)) + 1 : 1
    },
    size (node) {
      return node.children ? Math.max(1, node.children.reduce((acc, n) => acc + this.size(n), 0)) : 1
    },
    textWidth (node) {
      if (node.leaf) {
        return (1 + this.depth - node.depth) * this.graphWidth * this.labelRatio / this.depth
      }
      return this.graphWidth * this.labelRatio / this.depth
    }
  }
}
</script>



// WEBPACK FOOTER //
// node_modules/d3vue/components/HorizontalGroupedBar.vue